import React, { useState, useEffect } from "react";
import { useHeaderContext } from "../context/HeaderContext";
import { summarizeForHeader } from "../services/summarization";

// Helper to generate a Google Maps URL from a latLong string
function getGoogleMapsUrl(latLong) {
  if (!latLong || typeof latLong !== 'string') return undefined;
  const latMatch = latLong.match(/lat:\s*([\d.-]+)/i);
  const longMatch = latLong.match(/long:\s*([\d.-]+)/i);
  if (latMatch && longMatch) {
    const lat = latMatch[1];
    const lng = longMatch[1];
    return `https://www.google.com/maps?q=${lat},${lng}`;
  }
  return undefined;
}

const blmTool = {
  id: 'blm',
  name: 'BLM Recreation Points',
  icon: '🏕️',
  description: 'Find BLM campgrounds',
  workingId: 'blm1',
  endpoint: "https://gis.blm.gov/arcgis/rest/services/recreation/BLM_Natl_Recreation_Sites_Facilities/MapServer/0/query?where=FacilityTypeDescription='Campground'&outFields=FacilityName,FacilityDescription,FacilityReservationURL,State&f=geojson"
};

const BLMPage = () => {
  const { setGroqContext } = useHeaderContext();
  const [blmSelectedIdx, setBlmSelectedIdx] = useState("");
  const [blmItems, setBlmItems] = useState([]);
  const [blmLoading, setBlmLoading] = useState(false);
  const [blmError, setBlmError] = useState("");
  const [selectedBlmCampground, setSelectedBlmCampground] = useState(null);
  // Checkbox states
  const [showBlmPoints, setShowBlmPoints] = useState(true);
  const [showBlmNames, setShowBlmNames] = useState(false);

  useEffect(() => {
    setBlmLoading(true);
    setBlmError("");
    fetch(blmTool.endpoint)
      .then((res) => {
        if (!res.ok) throw new Error(`BLM fetch failed: ${res.status}`);
        return res.json();
      })
      .then((json) => {
        const features = Array.isArray(json?.features) ? json.features : [];
        const mapped = features.map((f, i) => {
          const p = f?.properties || {};
          return {
            id: i,
            name: p.FacilityName || "Unnamed Campground",
            description: (p.FacilityDescription || "").toString(),
            state: p.State || "",
            url: p.FacilityReservationURL || "",
          };
        });
        setBlmItems(mapped);
      })
      .catch((err) => setBlmError(err.message || "Failed to fetch BLM campgrounds."))
      .finally(() => setBlmLoading(false));
  }, []);

  useEffect(() => {
    if (blmSelectedIdx === "") {
      setSelectedBlmCampground(null);
      return;
    }
    const item = blmItems.find((x) => String(x.id) === String(blmSelectedIdx));
    if (item) {
      setSelectedBlmCampground(item);
      setGroqContext(item.name + (item.state ? ` (${item.state})` : ""));
    }
  }, [blmSelectedIdx, blmItems, setGroqContext]);

  function stripHtml(str) {
    try {
      const div = document.createElement("div");
      div.innerHTML = str;
      return (div.textContent || div.innerText || "").trim();
    } catch {
      return str;
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-green-900">
      <div className="bg-white dark:bg-gray-800 shadow-lg border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8">
          <div className="flex flex-col sm:flex-row items-center gap-3 text-center sm:text-left">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-600 rounded-xl flex items-center justify-center mb-2 sm:mb-0">
              <span className="text-2xl">{blmTool.icon}</span>
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white font-rye tracking-[0.1em]">
                {blmTool.name}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1 text-base sm:text-lg">
                Explore Bureau of Land Management campgrounds and details
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8">
        <div className="p-4 border rounded-xl bg-white dark:bg-gray-800">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">{`${blmTool.icon} ${blmTool.name}`}</h3>
          </div>
          {blmLoading && <div className="text-sm text-black">Loading BLM campgrounds…</div>}
          {blmError && <div className="text-sm text-red-600">Error: {blmError}</div>}
          {!blmLoading && !blmError && (
            <>
              {/* Responsive checkboxes above the dropdown */}
              <div className="flex flex-col sm:flex-row gap-4 mb-4 items-center">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={showBlmPoints}
                    onChange={e => setShowBlmPoints(e.target.checked)}
                    className="form-checkbox h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm font-medium">BLM Recreation Points</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={showBlmNames}
                    onChange={e => setShowBlmNames(e.target.checked)}
                    className="form-checkbox h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm font-medium">BLM Recreation Name</span>
                </label>
              </div>
              <label className="block text-sm font-medium mb-2">Choose a BLM campground:</label>
              <select
                className="w-full px-3 py-2 border rounded bg-white text-black"
                value={blmSelectedIdx}
                onChange={(e) => setBlmSelectedIdx(e.target.value)}
              >
                <option value="">-- Select a campground --</option>
                {blmItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} {item.state ? `(${item.state})` : ""}
                  </option>
                ))}
              </select>
              {selectedBlmCampground && (
                <div className="mt-3 text-sm bg-white text-black p-3 rounded">
                  <div className="font-semibold">{selectedBlmCampground.name}</div>
                  {selectedBlmCampground.state && <div className="text-gray-700">State: {selectedBlmCampground.state}</div>}
                  {selectedBlmCampground.url && (
                    <a
                      href={selectedBlmCampground.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 underline break-all"
                    >
                      Reservation / Info
                    </a>
                  )}
                  {selectedBlmCampground.description && (
                    <p className="mt-2 text-gray-800 line-clamp-4">{stripHtml(selectedBlmCampground.description)}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BLMPage;
